import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { AdminWebhookEvents } from "../components/AdminWebhookEvents";
import { AdminExperts } from "../components/AdminExperts";
import { AdminReviews } from "../components/AdminReviews";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Users, Sparkles, Coins, Euro, RotateCcw, Save, Plus, Trash2, ShieldCheck } from "lucide-react";

const inputCls = "border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm w-full focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white";
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "plans", label: "Plans" },
  { id: "packs", label: "Credit packs" },
  { id: "pricing", label: "Pricing rules" },
  { id: "webhooks", label: "Webhooks" },
  { id: "experts", label: "Human Experts" },
  { id: "reviews", label: "Review Requests" },
];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const int = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

const OverrideBadge = ({ active }) =>
  active ? (
    <span data-testid="override-active-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz uppercase tracking-wider bg-[#F59E0B]/10 text-[#F59E0B]">
      Override active
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz uppercase tracking-wider bg-slate-100 text-[#94A3B8]">
      Defaults
    </span>
  );

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-[#0B1B3D] grid place-items-center text-white"><Icon className="w-5 h-5" /></div>
      <p className="font-mono-kz text-xs text-[#94A3B8] tracking-widest font-bold uppercase">{label}</p>
    </div>
    <p className="font-display text-4xl font-black text-[#0B1B3D] mt-4">{value}</p>
    {sub && <p className="text-sm text-[#475569] mt-1">{sub}</p>}
  </div>
);

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [rules, setRules] = useState({});
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/admin/catalogs"), api.get("/admin/stats")]);
    setPlans(c.data.subscription_plans);
    setPacks(c.data.credit_packs);
    setRules(c.data.pricing_rules);
    setOverrides(c.data.overrides_active);
    setStats(s.data);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (user?.is_admin) load().catch(() => toast.error("Could not load admin data"));
  }, [user, load]);

  if (!authLoading && user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const saveCatalog = async (key, data) => {
    setSaving(true);
    try {
      await api.put(`/admin/catalogs/${key}`, { data });
      toast.success("Saved — changes are live immediately");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetCatalog = async (key) => {
    setSaving(true);
    try {
      await api.post(`/admin/catalogs/${key}/reset`);
      toast.success("Restored to defaults");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Admin panel">
      <div className="flex items-center gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold font-display transition-colors ${tab === t.id ? "bg-[#0B1B3D] text-white" : "bg-white border border-slate-200 text-[#475569] hover:border-[#0EA5E9]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {!loaded ? (
        <div className="text-[#94A3B8]">Loading…</div>
      ) : (
        <>
          {tab === "overview" && stats && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
              <StatCard icon={Users} label="Users" value={stats.users.total} sub={`+${stats.users.signups_30d} last 30 days`} testid="stat-users" />
              <StatCard icon={Sparkles} label="Sessions" value={stats.sessions.total} sub={`${stats.sessions.completed} completed · ${stats.sessions.running} running · ${stats.sessions.failed} failed`} testid="stat-sessions" />
              <StatCard icon={Coins} label="Credits consumed" value={stats.credits.consumed_total} sub="All time" testid="stat-credits" />
              <StatCard icon={Euro} label="Revenue" value={`€${stats.revenue.total_eur}`} sub={`${stats.revenue.paid_payments} paid purchases`} testid="stat-revenue" />
            </div>
          )}

          {tab === "plans" && (
            <CatalogSection
              title="Subscription plans" overrideActive={overrides.subscription_plans} saving={saving}
              onSave={() => saveCatalog("subscription_plans", plans.map((p) => ({ ...p, price_eur: num(p.price_eur), included_monthly_credits: int(p.included_monthly_credits), included_users: int(p.included_users) })))}
              onReset={() => resetCatalog("subscription_plans")} saveTestid="save-plans-btn" resetTestid="reset-plans-btn">
              <table className="w-full text-sm">
                <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
                  <th className="p-3">Plan</th><th className="p-3">Price €/mo</th><th className="p-3">Monthly credits</th><th className="p-3">Users</th>
                </tr></thead>
                <tbody>
                  {plans.map((p, i) => (
                    <tr key={p.plan_id} className="border-t border-slate-100">
                      <td className="p-3 font-display font-bold text-[#0B1B3D]">{p.name}<p className="font-mono-kz text-xs text-[#94A3B8] font-normal">{p.plan_id}</p></td>
                      <td className="p-3"><input data-testid={`plan-price-${p.plan_id}`} type="number" min="0" className={inputCls} value={p.price_eur ?? ""} placeholder="custom"
                        onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, price_eur: e.target.value } : x))} /></td>
                      <td className="p-3"><input data-testid={`plan-credits-${p.plan_id}`} type="number" min="0" className={inputCls} value={p.included_monthly_credits ?? ""} placeholder="custom"
                        onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, included_monthly_credits: e.target.value } : x))} /></td>
                      <td className="p-3"><input data-testid={`plan-users-${p.plan_id}`} type="number" min="1" className={inputCls} value={p.included_users ?? ""} placeholder="custom"
                        onChange={(e) => setPlans((ps) => ps.map((x, j) => j === i ? { ...x, included_users: e.target.value } : x))} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CatalogSection>
          )}

          {tab === "packs" && (
            <CatalogSection
              title="Credit packs" overrideActive={overrides.credit_packs} saving={saving}
              onSave={() => saveCatalog("credit_packs", packs.map((p) => ({ ...p, credits: int(p.credits), price_eur: num(p.price_eur), expiration_months: int(p.expiration_months) })))}
              onReset={() => resetCatalog("credit_packs")} saveTestid="save-packs-btn" resetTestid="reset-packs-btn"
              extraAction={
                <button data-testid="add-pack-btn" onClick={() => setPacks((ps) => [...ps, { pack_id: `pack_${Date.now()}`, name: "New pack", credits: 100, price_eur: 10, expiration_months: 12 }])}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0EA5E9] hover:text-[#0B1B3D]"><Plus className="w-4 h-4" /> Add pack</button>
              }>
              <table className="w-full text-sm">
                <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
                  <th className="p-3">Pack ID</th><th className="p-3">Name</th><th className="p-3">Credits</th><th className="p-3">Price €</th><th className="p-3">Expiry (months)</th><th className="p-3"></th>
                </tr></thead>
                <tbody>
                  {packs.map((p, i) => (
                    <tr key={p.pack_id} className="border-t border-slate-100">
                      <td className="p-3 font-mono-kz text-xs text-[#475569]">{p.pack_id}</td>
                      <td className="p-3"><input data-testid={`pack-name-${i}`} className={inputCls} value={p.name} onChange={(e) => setPacks((ps) => ps.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></td>
                      <td className="p-3"><input data-testid={`pack-credits-${i}`} type="number" min="1" className={inputCls} value={p.credits} onChange={(e) => setPacks((ps) => ps.map((x, j) => j === i ? { ...x, credits: e.target.value } : x))} /></td>
                      <td className="p-3"><input data-testid={`pack-price-${i}`} type="number" min="1" step="0.01" className={inputCls} value={p.price_eur} onChange={(e) => setPacks((ps) => ps.map((x, j) => j === i ? { ...x, price_eur: e.target.value } : x))} /></td>
                      <td className="p-3"><input data-testid={`pack-expiry-${i}`} type="number" min="1" className={inputCls} value={p.expiration_months} onChange={(e) => setPacks((ps) => ps.map((x, j) => j === i ? { ...x, expiration_months: e.target.value } : x))} /></td>
                      <td className="p-3"><button data-testid={`pack-remove-${i}`} onClick={() => setPacks((ps) => ps.filter((_, j) => j !== i))} className="text-[#EF4444] hover:bg-[#EF4444]/10 p-1.5 rounded-md"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CatalogSection>
          )}

          {tab === "pricing" && (
            <CatalogSection
              title="Pricing rules" overrideActive={overrides.pricing_rules} saving={saving}
              onSave={() => saveCatalog("pricing_rules", Object.fromEntries(Object.entries(rules).map(([k, r]) => [k, { ...r, base_credit_cost: int(r.base_credit_cost) }])))}
              onReset={() => resetCatalog("pricing_rules")} saveTestid="save-pricing-btn" resetTestid="reset-pricing-btn">
              <table className="w-full text-sm">
                <thead><tr className="text-left font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest">
                  <th className="p-3">Feature</th><th className="p-3">Credit cost</th><th className="p-3">Minimum plan</th>
                </tr></thead>
                <tbody>
                  {Object.entries(rules).map(([key, r]) => (
                    <tr key={key} className="border-t border-slate-100">
                      <td className="p-3 font-display font-bold text-[#0B1B3D]">{r.label}<p className="font-mono-kz text-xs text-[#94A3B8] font-normal">{key}</p></td>
                      <td className="p-3"><input data-testid={`rule-cost-${key}`} type="number" min="0" className={inputCls} value={r.base_credit_cost}
                        onChange={(e) => setRules((rs) => ({ ...rs, [key]: { ...rs[key], base_credit_cost: e.target.value } }))} /></td>
                      <td className="p-3">
                        <select data-testid={`rule-plan-${key}`} className={inputCls} value={r.plan_minimum}
                          onChange={(e) => setRules((rs) => ({ ...rs, [key]: { ...rs[key], plan_minimum: e.target.value } }))}>
                          {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CatalogSection>
          )}
          {tab === "webhooks" && <AdminWebhookEvents />}
          {tab === "experts" && <AdminExperts />}
          {tab === "reviews" && <AdminReviews />}
        </>
      )}
    </DashboardLayout>
  );
}

const CatalogSection = ({ title, overrideActive, children, onSave, onReset, saving, saveTestid, resetTestid, extraAction }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-4 h-4 text-[#0EA5E9]" />
        <h2 className="font-display font-extrabold text-[#0B1B3D]">{title}</h2>
        <OverrideBadge active={overrideActive} />
      </div>
      <div className="flex items-center gap-3">
        {extraAction}
        <button data-testid={resetTestid} onClick={onReset} disabled={saving || !overrideActive}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#475569] hover:text-[#0B1B3D] disabled:opacity-40 border border-slate-200 px-3 py-1.5 rounded-lg">
          <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
        </button>
        <button data-testid={saveTestid} onClick={onSave} disabled={saving}
          className="inline-flex items-center gap-1.5 bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 text-white text-sm font-bold px-4 py-1.5 rounded-lg font-display">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
    <p className="px-6 pt-3 text-xs text-[#94A3B8]">Changes apply immediately to all users — no redeploy needed.</p>
    <div className="p-3">{children}</div>
  </div>
);
